// Sauna / basement corridor drywall takeoff for Kimbark Residence.
// Source: ~/Downloads/labels_aligned/takeoff_report/*.txt (Stefan SI extraction).
// Coordinates: raw Z-up space — world_x=raw_x, world_y=raw_z, world_z=-raw_y.
// Quad point order per surface: as extracted (P0 top-right or top-left depending on
// face orientation). WallTakeoffOverlay applies SPLAT_QUATERNION to groups.

export type WallStatus = 'complete' | 'remaining';

export interface WallSurface {
  id: string;
  label: string;
  status: WallStatus;
  points: [number, number, number][]; // 4 raw Z-up corners
  widthFt: number;
  heightFt: number;
  areaSqFt: number;
}

export interface WallOpening {
  id: string;
  widthIn: number;
  heightIn: number;
  areaSqFt: number;
  points: [number, number, number][];
}

export interface WallElectrical {
  id: string;
  type: 'outlet' | 'switch';
  points: [number, number, number][];
}

export interface WallTakeoffData {
  title: string;
  subtitle: string;
  color: string;
  surfaces: WallSurface[];
  openings: WallOpening[];
  electrical: WallElectrical[];
  cameraPos: [number, number, number];
  cameraLookAt: [number, number, number];
  drywallSpec: string;
  sheetSqFt: number;
  wastePercent: number;
}

export const KIMBARK_BASEMENT_WALL_TAKEOFF: WallTakeoffData = {
  title: 'Basement Wall Takeoff',
  subtitle: 'Sauna Room',
  color: '#a855f7',

  surfaces: [
    {
      id: 'wall_left',
      label: 'Left wall',
      status: 'complete',
      points: [
        [1.3852322600455906, -5.5409670101579795, -1.6552730814714498],
        [1.6365996662428506, -4.8357798570784905, -1.6350923167128100],
        [1.6255454001197602, -4.8254478847612301, -3.8436446247821801],
        [1.3881883799352002, -5.5161000321138998, -3.8818202666069803],
      ],
      widthFt: 2.5,
      heightFt: 7.3,
      areaSqFt: 17.7,
    },
    {
      id: 'wall_middle',
      label: 'Middle wall',
      status: 'remaining',
      points: [
        [1.1343072646184500, -6.2746573399657191, -1.6755526081665897],
        [0.7402969172991503, -7.3412856125244197, -1.6968206813714097],
        [0.7405956484487803, -7.3252003146283311, -3.8693070149721995],
        [1.1088403490757806, -6.2504815135448704, -3.8299529711555800],
      ],
      widthFt: 3.7,
      heightFt: 7.1,
      areaSqFt: 26.5,
    },
    {
      id: 'wall_right',
      label: 'Right wall',
      status: 'complete',
      points: [
        [0.4590558771502700, -8.1410821768663588, -1.6992649764914298],
        [0.1497869522544404, -8.7901169329199895, -1.7282023739143000],
        [0.0555781893962704, -8.7442627663515804, -3.8644220703365901],
        [0.4476093844611104, -8.1024024239431505, -3.8626456400271501],
      ],
      widthFt: 2.4,
      heightFt: 7.0,
      areaSqFt: 17.0,
    },
  ],

  openings: [
    {
      id: 'door_left',
      widthIn: 30,
      heightIn: 87,
      areaSqFt: 18.3,
      points: [
        [1.3825146502607506, -5.5495622149592103, -1.6552996890532898],
        [1.1343072646184500, -6.2746573399657191, -1.6755526081665897],
        [1.1174155155188004, -6.2531369174205107, -3.8480010633854205],
        [1.3710079365267300, -5.5106959866892398, -3.8727678117055895],
      ],
    },
    {
      id: 'door_right',
      widthIn: 33,
      heightIn: 86,
      areaSqFt: 19.6,
      points: [
        [0.7402969172991503, -7.3412856125244197, -1.6968206813714097],
        [0.4590558771502700, -8.1410821768663588, -1.6992649764914298],
        [0.4476093844611104, -8.1024024239431505, -3.8626456400271501],
        [0.7405956484487803, -7.3252003146283311, -3.8693070149721995],
      ],
    },
  ],

  electrical: [
    {
      id: 'plug_left',
      type: 'outlet',
      points: [
        [1.4918891359139503, -5.1908186563189398, -3.4931823881615696],
        [1.4669882370802707, -5.2396723228017601, -3.4933230944616298],
        [1.5006048181283100, -5.1939091693370196, -3.3850264091193902],
        [1.4815815496344302, -5.2540756029456297, -3.3852126621922700],
      ],
    },
    {
      id: 'switch_left',
      type: 'switch',
      points: [
        [1.5101535581298999, -5.1995792579852802, -2.5286605789645198],
        [1.4825350495113803, -5.2570281292693295, -2.5288278928464201],
        [1.4796969976368204, -5.2652503833770403, -2.6370294186614198],
        [1.5100230791993701, -5.1991752280673005, -2.6458500737171100],
      ],
    },
    {
      id: 'switch_center',
      type: 'switch',
      points: [
        [0.8366747378614603, -7.0002160612814297, -2.6062362749511001],
        [0.8642932464799800, -6.9427671899973795, -2.6060689610691998],
        [0.8644237254105103, -6.9431712199153601, -2.4888794663166101],
        [0.8340876070071506, -7.0092152960006402, -2.4890733877803499],
      ],
    },
    {
      id: 'switch_right',
      type: 'switch',
      points: [
        [0.3526412252804203, -8.3222937077970691, -2.5381378712258398],
        [0.3309003470017005, -8.3910553462069100, -2.5383507318805600],
        [0.3307698680711706, -8.3906513162889311, -2.6555402266331498],
        [0.3439155062252506, -8.3191721155545295, -2.6553084267874496],
      ],
    },
    {
      id: 'plug_right',
      type: 'outlet',
      points: [
        [0.3050158377686403, -8.4368632486926494, -3.4219199370847599],
        [0.2724045203505607, -8.5400057063074097, -3.4222392280668399],
        [0.2781516717598302, -8.5509144435152216, -3.5394742695922496],
        [0.3048853588381104, -8.4364592187746705, -3.5391094318373497],
      ],
    },
  ],

  // Camera matches Sauna room viewpoint in kimbarkRooms.ts.
  cameraPos:    [-1.533, -2.491, 5.599],
  cameraLookAt: [4.583, -3.359, 10.683],

  drywallSpec: "4' × 8' × 1/2\"",
  sheetSqFt: 32,
  wastePercent: 10,
};
